import React, { Component } from "react";
import { Redirect } from "react-router-dom";
import jwt_decode from "jwt-decode"; 
import msalInstance from "./msalConfig";
import Title from "../../template/title";
import Logo from "../../assets/images/cellere-icon.png";
import authAPI from "../../auth/authAPI";
import { getToken } from "../../auth/auth"; 
import "./login.css";

class Login extends Component {
  _isMounted = false;

  constructor() {
    super();
    this.state = {
        user: null,
        redirect: false,
        redirectFor: "",
        isAuthenticating: false,
        error: null,
        auth: false,
        loginMethod: null, 
    };
  }

  componentDidMount() {
    this._isMounted = true;
    document.body.classList.add("login-page");
    
    window.addEventListener('message', this.handlePostMessage);
    
    const token = getToken();
    
    if (token) {
        try {
            const user = jwt_decode(token);
            if (this._isMounted) {
                this.setState({ user, auth: true });
            }
            return; 
        } catch (error) {
            console.error("Erro ao decodificar token:", error);
            this.clearStoredTokens();
        }
    }

    this.handleRedirectCallback();
  }

  componentWillUnmount() {
    this._isMounted = false;
    document.body.classList.remove("login-page");
    window.removeEventListener('message', this.handlePostMessage);
  }

  handlePostMessage = (event) => {
    if (event.origin !== window.location.origin) {
      return;
    }

    if (event.data.type === 'MSAL_REDIRECT_SUCCESS') {
      console.log("Recebido evento de sucesso do MSAL:", event.data.response);
      this.processRedirectSuccess(event.data.response);
    } else if (event.data.type === 'MSAL_REDIRECT_ERROR') {
      console.error("Recebido evento de erro do MSAL:", event.data.error);
      this.handleLoginError(event.data.error);
    }
  }

  processRedirectSuccess = async (response) => {
    try {
      this.setState({ isAuthenticating: true });
      
      if (response && response.account) {
        await this.acquireTokenAndAuthenticate(response.account);
      } else {
        // Tentar obter conta das contas existentes
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          await this.acquireTokenAndAuthenticate(accounts[0]);
        } else {
          throw new Error("Nenhuma conta encontrada após redirect");
        }
      }
    } catch (error) {
      console.error("Erro ao processar sucesso do redirect:", error);
      this.handleLoginError(error);
    }
  }

  clearStoredTokens = () => {
    localStorage.removeItem("@timesheet-token");
    sessionStorage.removeItem("@timesheet-token");
  }

  handleRedirectCallback = async () => {
    try {
      // Verificar se estamos retornando de um redirect
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const hash = window.location.hash;
      
      // Verificar se há parâmetros de callback no hash ou query
      if (code && state) {
        console.log("Callback de redirect detectado com code/state");
        this.setState({ isAuthenticating: true });
        await this.processAuthCallback();
      } else if (hash && (hash.includes('id_token') || hash.includes('access_token'))) {
        console.log("Callback de redirect detectado no hash");
        this.setState({ isAuthenticating: true });
        await this.processAuthCallback();
      } else {
        // Verificar se há contas MSAL já logadas
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          console.log("Conta MSAL encontrada após redirect, tentando autenticar");
          this.setState({ isAuthenticating: true });
          
          // Aguardar um pouco para garantir que o MSAL processou tudo
          setTimeout(async () => {
            try {
              await this.acquireTokenAndAuthenticate(accounts[0]);
            } catch (error) {
              console.error("Erro ao processar conta MSAL:", error);
              this.setState({ 
                error: "Erro ao processar conta encontrada",
                isAuthenticating: false 
              });
            }
          }, 1000);
        }
      }
    } catch (error) {
      console.error("Erro ao processar callback de redirect:", error);
      this.setState({ 
        error: "Erro ao processar retorno da autenticação",
        isAuthenticating: false 
      });
    }
  }

  processAuthCallback = async () => {
    try {
      // Aguardar um pouco para o MSAL processar o callback
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        await this.acquireTokenAndAuthenticate(accounts[0]);
      } else {
        throw new Error("Nenhuma conta encontrada após callback");
      }
    } catch (error) {
      console.error("Erro ao processar callback:", error);
      this.setState({ 
        error: "Erro ao processar autenticação",
        isAuthenticating: false 
      });
    }
  }
  componentWillUnmount() {
    this._isMounted = false;
    document.body.classList.remove("login-page");
  }

  // Detectar se é um dispositivo móvel ou se há problemas com popups
  isMobileOrPopupBlocked = () => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    return isMobile || isIOS;
  }

  handleAzureLogin = async () => {
    if (this.state.isAuthenticating) {
      return; // Evitar múltiplas tentativas simultâneas
    }

    this.setState({ isAuthenticating: true, error: null });

    const loginRequest = {
      scopes: ["3f43a622-8015-4119-8cf9-ff427c1f6396/.default"],
      prompt: "select_account", // Sempre mostrar seleção de conta
    };

    try {
      // Limpar contas existentes para evitar conflitos
      const accounts = msalInstance.getAllAccounts();
      
      let account = null;
      let tokenResponse = null;

      // Verificar se já existe uma conta logada
      if (accounts.length > 0) {
        account = accounts[0];
        
        // Tentar adquirir token silenciosamente primeiro
        try {
          tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: ["3f43a622-8015-4119-8cf9-ff427c1f6396/.default"],
            account,
          });
        } catch (silentError) {
          console.log("Token silencioso falhou, prosseguindo com login interativo");
          account = null; // Forçar novo login
        }
      }

      // Se não temos token ou conta, fazer login interativo
      if (!tokenResponse) {
        await this.performInteractiveLogin(loginRequest);
        return;
      }

      // Se chegamos aqui, temos um token válido
      await this.authenticateWithBackend(tokenResponse.accessToken || tokenResponse.idToken);

    } catch (error) {
      console.error("Erro de login:", error);
      this.handleLoginError(error);
    }
  };

  performInteractiveLogin = async (loginRequest) => {
    try {
      let response;
      
      // Usar redirect em dispositivos móveis ou se popup foi bloqueado
      if (this.isMobileOrPopupBlocked()) {
        console.log("Usando redirect para login (dispositivo móvel detectado)");
        this.setState({ loginMethod: 'redirect' });
        await msalInstance.loginRedirect(loginRequest);
        return; // O redirect irá recarregar a página
      } else {
        console.log("Usando popup para login");
        this.setState({ loginMethod: 'popup' });
        
        try {
          response = await msalInstance.loginPopup(loginRequest);
        } catch (popupError) {
          console.warn("Popup bloqueado, tentando redirect...");
          this.setState({ loginMethod: 'redirect' });
          await msalInstance.loginRedirect(loginRequest);
          return;
        }
      }

      if (response && response.account) {
        await this.acquireTokenAndAuthenticate(response.account);
      }
    } catch (error) {
      console.error("Erro no login interativo:", error);
      throw error;
    }
  };

  acquireTokenAndAuthenticate = async (account) => {
    try {
      console.log("Iniciando aquisição de token para conta:", account.userName);
      
      const tokenRequest = {
        scopes: ["3f43a622-8015-4119-8cf9-ff427c1f6396/.default"],
        account,
      };

      let tokenResponse;
      
      try {
        console.log("Tentando adquirir token silenciosamente...");
        tokenResponse = await msalInstance.acquireTokenSilent(tokenRequest);
        console.log("Token silencioso obtido com sucesso");
      } catch (error) {
        console.warn("Token silencioso falhou:", error);
        
        if (this.isMobileOrPopupBlocked()) {
          console.log("Usando redirect para token...");
          await msalInstance.acquireTokenRedirect(tokenRequest);
          return;
        } else {
          try {
            console.log("Tentando popup para token...");
            tokenResponse = await msalInstance.acquireTokenPopup(tokenRequest);
            console.log("Token popup obtido com sucesso");
          } catch (popupError) {
            console.warn("Popup bloqueado, usando redirect...");
            await msalInstance.acquireTokenRedirect(tokenRequest);
            return;
          }
        }
      }

      const token = tokenResponse.accessToken || tokenResponse.idToken;
      console.log("Token obtido, autenticando com backend...");
      await this.authenticateWithBackend(token);
      
    } catch (error) {
      console.error("Erro ao adquirir token:", error);
      throw error;
    }
  };

  authenticateWithBackend = async (token) => {
    if (!token) {
      throw new Error("Nenhum token válido foi recebido do Azure AD");
    }

    try {
      console.log("Verificando saúde do backend...");
      const backendHealthy = await this.checkBackendHealth();
      
      if (!backendHealthy) {
        console.warn("Backend não está respondendo, tentando autenticação mesmo assim...");
      }

      console.log("Autenticando com backend...");
      const backendResponse = await authAPI.post(
        "/login",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 10000, // 10 segundos de timeout
        }
      );

      console.log("Resposta do backend:", backendResponse.data);

      if (!backendResponse.data || !backendResponse.data.token) {
        throw new Error("Token inválido retornado do backend");
      }

      const finalToken = backendResponse.data.token;
      console.log("Token de autenticação recebido, armazenando...");
      
      // Armazenar o token
      sessionStorage.setItem("@timesheet-token", finalToken);

      console.log("Autenticação concluída com sucesso, redirecionando...");
      
      if (this._isMounted) {
        this.setState({
          auth: true,
          isAuthenticating: false,
          error: null,
        });
      }
    } catch (backendError) {
      console.error("Erro na comunicação com o backend:", backendError);
      
      let errorMessage = "Erro de conexão com o servidor";
      
      if (backendError.response) {
        switch (backendError.response.status) {
          case 401:
            errorMessage = "Credenciais inválidas. Tente fazer login novamente.";
            // Limpar tokens em caso de 401
            this.clearStoredTokens();
            break;
          case 403:
            errorMessage = "Acesso negado. Verifique suas permissões.";
            break;
          case 404:
            errorMessage = "Serviço de autenticação não encontrado. Verifique se o servidor está rodando.";
            break;
          case 500:
            errorMessage = "Erro interno do servidor. Tente novamente em alguns minutos.";
            break;
          default:
            errorMessage = `Erro do servidor: ${backendError.response.status}`;
        }
      } else if (backendError.code === 'ECONNABORTED') {
        errorMessage = "Timeout na conexão com o servidor. Verifique sua conexão.";
      } else if (backendError.code === 'ECONNREFUSED') {
        errorMessage = "Não foi possível conectar ao servidor. Verifique se o servidor está rodando.";
      }
      
      throw new Error(errorMessage);
    }
  };

  // Verificar se o backend está funcionando
  checkBackendHealth = async () => {
    try {
      const response = await authAPI.get('/health');
      return response.status === 200;
    } catch (error) {
      console.warn("Backend não está respondendo:", error);
      return false;
    }
  };

  handleLoginError = (error) => {
    let errorMessage = "Erro desconhecido na autenticação";
    
    if (error.message) {
      errorMessage = error.message;
    } else if (error.errorCode) {
      switch (error.errorCode) {
        case "user_cancelled":
          errorMessage = "Login cancelado pelo usuário";
          break;
        case "popup_window_error":
          errorMessage = "Erro na janela de popup. Tente novamente.";
          break;
        case "interaction_required":
          errorMessage = "Interação necessária. Tente fazer login novamente.";
          break;
        default:
          errorMessage = `Erro de autenticação: ${error.errorCode}`;
      }
    }

    if (this._isMounted) {
      this.setState({
        error: errorMessage,
        isAuthenticating: false,
      });
    }
  };

  // Método para retry do login em caso de erro
  retryLogin = () => {
    this.setState({ error: null });
    this.handleAzureLogin();
  };

  render() {
    if (this.state.auth) {
      return <Redirect to="/loading" />;
    }

    return (
      <div className="wrapper">
        <Title name="Login" />
        <div className="row animated fadeIn up-part">
          <div id="panel" className="col-md-6 col-md-offset-4">
            <div className="panel text-center loginscreen">
              <div>
                <div className="text-center">
                  <img className="login-logo" src={Logo} alt="Cellere Logo" />
                </div>
                <h3 className="welcome-text">Bem-vindo</h3>
                <p>Entre com seu usuário Microsoft para acessar o sistema de timesheet.</p>

                <button
                  id="btn-login"
                  className="btn btn-primary block full-width m-b"
                  onClick={this.handleAzureLogin}
                  disabled={this.state.isAuthenticating}
                  aria-label={this.state.isAuthenticating ? 'Autenticando...' : 'Fazer login com Microsoft'}
                >
                  {this.state.isAuthenticating ? (
                    <div className="d-flex align-items-center justify-content-center">
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span>{this.state.loginMethod === 'redirect' ? 'Redirecionando...' : 'Autenticando...'}</span>
                    </div>
                  ) : (
                    <span>
                      <i className="fas fa-sign-in-alt me-2" aria-hidden="true"></i>
                      Login com Microsoft
                    </span>
                  )}
                </button>

                {this.state.error && (
                  <div className="alert alert-danger animated fadeIn" role="alert">
                    <div className="error-message text-center">
                      <i className="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
                      {this.state.error}
                    </div>
                    <div className="text-center">
                      <button 
                        className="btn btn-sm btn-outline-danger"
                        onClick={this.retryLogin}
                        disabled={this.state.isAuthenticating}
                        aria-label="Tentar fazer login novamente"
                      >
                        <i className="fas fa-redo me-1" aria-hidden="true"></i>
                        Tentar novamente
                      </button>
                    </div>
                  </div>
                )}

                {this.state.isAuthenticating && (
                  <div className="login-status" role="status" aria-live="polite">
                    <div className="progress" aria-label="Progresso da autenticação">
                      <div className="progress-bar progress-bar-striped progress-bar-animated" 
                           role="progressbar" 
                           style={{width: '100%'}} 
                           aria-valuenow="100" 
                           aria-valuemin="0" 
                           aria-valuemax="100">
                      </div>
                    </div>
                    <small className="text-center">
                      {this.state.loginMethod === 'redirect' 
                        ? 'Você será redirecionado para o login da Microsoft...' 
                        : 'Processando autenticação, aguarde...'}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Login;